import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgProgress } from '@ngx-progressbar/core';
import { ToastrService } from 'ngx-toastr';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { ListeableComponent } from '../../../../shared/components/interfaces/listeable.component';
import { ResulteableComponent } from '../../../../shared/components/interfaces/resulteable.component';
import {
  KEYSTROKE,
  MiscellaneousUtils,
  PREVENT_KEYUP_EVENTS,
  SEARCH_QUITE_PERIOD
} from '../../../../shared/utils/miscellaneous.utils';
import { AuthService } from '../../../security/service/auth.service';
import { ServiceProviderUserListColumnsModel } from '../../model/serviceprovider-user-list-columns.model';
import { UserListColumnsModel } from '../../model/user-list-columns.model';
import {
  convertToHtmlContent,
  CustomerModel,
  UserModel
} from '../../model/user.model';
import { UserManagerListColumnsModel } from '../../model/usermanager-user-list';
import { UsersService } from '../../service/users.service';
@Component({
  selector: 'user-list',
  templateUrl:
    './console/app-js/modules/user/components/list/user-list.component.html'
})
export class UserListComponent extends APIOperableComponent
  implements ListeableComponent, ResulteableComponent, OnInit {
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  columns = UserListColumnsModel;
  private originalData: Array<UserModel>;
  data: Array<UserModel> = [];
  noRecordsSettings;
  filterValue: string;
  private typingTimeout: any;
  private endCursor: string = null;
  private hasNextPage: Boolean = false;
  private infiniteScroll: Boolean = false;
  private filterSearch: Boolean = true;
  private personaList: Array<any>;
  private groupsOnly: Array<any>;
  public openUserModal = false;
  public userModalTitle: string;
  public user: UserModel;

  constructor(
    progressService: NgProgress,
    toastr: ToastrService,
    authService: AuthService,
    private usersService: UsersService
  ) {
    super(progressService, toastr, authService);
    this.setDefaultNoRecordSettings();
    if (this.authService.isEnterpriseSPUserManager()) {
      this.columns = ServiceProviderUserListColumnsModel;
    } else if (this.authService.isUsersManagerAdmin()) {
      this.columns = UserManagerListColumnsModel;
    } else {
      this.columns = UserListColumnsModel;
    }
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.searchInput.nativeElement.focus();
    }, 100);
  }

  /**
   * Filter Users based on the input search value.
   * @param $event
   */
  onFilterData($event): void {
    this.filterSearch = true;
    this.endCursor = null;
    if ($event.code === KEYSTROKE.ENTER) {
      this.onSearch();
    } else if (!PREVENT_KEYUP_EVENTS.test($event.code)) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(
        () => this.onSearch(),
        SEARCH_QUITE_PERIOD
      );
    }
  }

  /**
   * On Row cell click handler.
   * @param $event
   */
  onCellClick($event): void {
    const user = this.data.find(x => $event.dataItem.id === x.id);
    this.onEditUser(user);
  }

  /**
   * Open the Edit User modal.
   * @param user
   */
  onEditUser(user: UserModel): void {
    user.metadata.customer = user.metadata.customer
      ? user.metadata.customer
      : new CustomerModel();
    this.userModalTitle = 'Edit User';
    this.user = user;
    this.openUserModal = true;
  }

  public closedUserModal(event) {
    this.openUserModal = false;
    if (event && event.isActionPerformed) {
      setTimeout(() => {
        const keypress = new KeyboardEvent('keypress', {
          key: 'Enter',
          code: 'Enter'
        });
        this.onFilterData(keypress);
      }, 300);
    }
  }

  /**
   * Open the Edit User groups modal.
   * @param user
   */
  onAddUser(): void {
    this.userModalTitle = 'Add User';
    this.user = new UserModel();
    this.openUserModal = true;
  }

  /**
   * Get User List
   */
  onSearch(): void {
    if (!this.filterValue || this.filterValue.length < 3) {
      this.onReset();
      return;
    }
    this.data = [];
    this.loadingIndicator = true;
    this.setDefaultNoRecordSettings();
    this.usersService.getGroups().subscribe(result => {
      this.personaList = result.groups.filter(
        personaElement => personaElement.type === 'persona'
      );

      this.groupsOnly = result.groups.filter(
        groupsElement => groupsElement.type === 'group'
      );

      this.getUsers();
    });
  }

  getUsers(): void {
    this.usersService.getUsers(this.filterValue, this.endCursor).subscribe(
      result => {
        result.users.forEach(user => {
          const groupNames = this.groupsOnly
            .filter(p => user.groups.find(g => p.name === g.name))
            .map(gp => gp.name);

          const personaNames = this.personaList
            .filter(p => user.groups.find(g => p.persona === g.name))
            .map(ps => ps.name);

          user.groupsAsHtml = convertToHtmlContent(groupNames, true);
          if (user.metadata.serviceProvider) {
            if (user.metadata.serviceProvider.serviceProviders) {
              const serviceProviderNumbers = user.metadata.serviceProvider.serviceProviders.map(
                spNumber => {
                  if (spNumber && spNumber.number != undefined) {
                    return spNumber.number;
                  }
                }
              );
              if (serviceProviderNumbers) {
                user.serviceProviderNumbersAsHtml = convertToHtmlContent(
                  serviceProviderNumbers,
                  false
                );
              }
              const serviceProviderNames = user.metadata.serviceProvider.serviceProviders.map(
                spName => {
                  if (spName && spName.name != undefined) {
                    return spName.name;
                  }
                }
              );
              if (serviceProviderNames) {
                user.serviceProviderNamesAsHtml = convertToHtmlContent(
                  serviceProviderNames,
                  false
                );
              }
            }
          }
          user.personaAsHtml = convertToHtmlContent(personaNames, false);
          const customerNumbers = MiscellaneousUtils.safeAccess(
            user,
            'metadata.customer.customerNumbers'
          );
          if (customerNumbers) {
            user.customerNumbersAsHtml = convertToHtmlContent(
              customerNumbers,
              true
            );
          }
        });
        this.originalData = result.users;
        let dataCheck = JSON.parse(JSON.stringify(this.data));
        if (dataCheck !== null && this.infiniteScroll) {
          dataCheck.push(...this.originalData);
          this.data = [...dataCheck];
          this.infiniteScroll = false;
        } else if (this.filterSearch === true) {
          this.data = [...this.originalData];
          this.filterSearch = false;
        }
        if (this.originalData.length === 0) {
          this.noRecordsSettings.message = 'No Results Found';
          this.noRecordsSettings.styleClass = null;
        }
        this.loadingIndicator = false;
        this.endCursor = result.endCursor;
        this.hasNextPage = result.hasNextPage;
        setTimeout(() => {
          this.searchInput.nativeElement.focus();
        }, 100);
      },
      error => {
        console.error(error);
        this.loadingIndicator = false;
        this.data = [];
      }
    );
  }

  /**
   * Clears the grid data results.
   */
  onReset(): void {
    this.data = [];
    this.setDefaultNoRecordSettings();
    this.endCursor = null;
    this.hasNextPage = false;
  }

  /**
   * Set No Records Settings to Default state.
   */
  private setDefaultNoRecordSettings(): void {
    this.noRecordsSettings = {
      message: '',
      styleClass: 'no-records-wrapper'
    };
  }

  /**
   * On Add button click event.
   */
  onAddItem(): void {
    /** Not implemented yet **/
  }

  /**
   * Not implemented yet
   */
  onItemDetail(): void {
    /** Not implemented yet **/
  }

  /**
   * Not implemented yet
   */
  onCollapseRow(): void {
    /** Not implemented yet **/
  }

  /**
   * Not implemented yet
   */
  onExpandRow(): void {
    /** Not implemented yet **/
  }

  /**
   * Not implemented yet
   */
  onDeleteItems(): void {
    /** Not implemented yet **/
  }

  /**
   * Not implemented yet
   */
  onFullscreenMode(): void {
    /** Not implemented yet **/
  }

  /**
   * Infinity scrolling pagination functionality
   */
  infinityScroll(): void {
    if (this.hasNextPage) {
      this.infiniteScroll = true;
      this.onSearch();
    }
  }
}
